import clsx from 'clsx';
import React from 'react';
import { match } from 'ts-pattern';

import type { TextSizeType } from '../utils';
import { Icon } from '../Icon';
import { TextInput } from '../Input/TextInput';
import { Tooltip } from '../Tooltip';
import { sizeToTextClassName } from '../utils';

export const kBoxWidth =
  'w-f15 min-w-f15 largeLaptop:w-f19 largeLaptop:min-w-f19';
/** 1.5× kBoxWidth for label columns (e.g. Issued, Total, Total Payins). */
export const kLabelColumnWidth =
  'w-[11.25lh] min-w-[11.25lh] largeLaptop:w-[14.25lh] largeLaptop:min-w-[14.25lh]';
export const kHalfBoxWidth =
  'w-f7 min-w-f7 largeLaptop:w-f10 largeLaptop:min-w-f10';

type ItemTypeCellProps = {
  itemType: 'payin' | 'payout';
};

export const ItemTypeCell = ({ itemType }: ItemTypeCellProps) => (
  <div
    className={clsx([
      kHalfBoxWidth,
      'grid grid-cols-2 h-full border-main-200 border-r-[1px]',
    ])}
  >
    <Tooltip
      tooltipComponent={<span className="text-main">Payin</span>}
      baseComponent={
        <div className="flex w-full items-center justify-center border-main-200 border-r-[1px] h-full">
          {itemType === 'payin' && (
            <Icon type="up" size="md" primaryColor="asset-green" />
          )}
        </div>
      }
      tooltipDisabled={itemType === 'payout'}
    />
    <Tooltip
      tooltipComponent={<span className="text-main">Payout</span>}
      baseComponent={
        <div className="flex w-full items-center justify-center h-full">
          {itemType === 'payout' && (
            <Icon type="down" size="md" primaryColor="liability-red" />
          )}
        </div>
      }
      tooltipDisabled={itemType === 'payin'}
    />
  </div>
);

type TextCellProps = {
  text: string;
  textType?: 'primary' | 'secondary' | 'warning';
  textSize: TextSizeType;
  align: 'left' | 'right' | 'center';
  border: 'left' | 'right' | 'none';
  onChange?: (text: string) => void;
  fullWidth?: boolean;
  /** Use for label column (e.g. Total); 1.5× standard width. */
  width?: 'standard' | 'label';
  useMonoText?: boolean;
  'data-testid'?: string;
};

export const TextCell = ({
  text,
  textType = 'primary',
  textSize,
  align,
  border,
  onChange,
  fullWidth = false,
  width = 'standard',
  useMonoText = false,
  'data-testid': testId,
}: TextCellProps) => (
  <div
    className={clsx([
      'text-md flex flex-col h-full min-h-full p-f1 box-border',
      fullWidth && 'w-full min-w-f19',
      !fullWidth && width === 'label' && kLabelColumnWidth,
      !fullWidth && width !== 'label' && kBoxWidth,
      border === 'left' && 'border-main-200 border-l-[1px]',
      border === 'right' && 'border-main-200 border-r-[1px]',
      border === 'none' && 'border-none',
      align === 'left' && 'justify-start',
      align === 'center' && 'justify-center',
      align === 'right' && 'justify-start',
      textType === 'primary' && 'text-main',
      textType === 'secondary' && 'text-main-500',
      textType === 'warning' && 'text-orange',
      useMonoText ? 'font-FragmentMono' : 'font-sans',
    ])}
  >
    {match(onChange)
      .when(
        (fn): fn is (text: string) => void => fn !== undefined,
        (fn) => (
          <TextInput
            value={text}
            size={textSize}
            padding="p-0"
            width="w-full"
            height="h-fit"
            align={align}
            placeholder="Enter Invoice ID"
            onChange={(e) => fn(e.target.value)}
            data-testid={testId}
          />
        )
      )
      .otherwise(() => (
        <span
          className={clsx(
            sizeToTextClassName[textSize],
            align === 'left' && 'text-left',
            align === 'center' && 'text-center',
            align === 'right' && 'text-right'
          )}
          data-testid={testId}
        >
          {text}
        </span>
      ))}
  </div>
);

type LayoutCellProps = {
  children: React.ReactNode;
  border: 'left' | 'right' | 'none';
  align?: 'top' | 'center' | 'bottom';
  justify?: 'start' | 'center' | 'end';
  width?: 'half' | 'standard' | 'full';
  removePadding?: boolean;
};

export const LayoutCell = ({
  children,
  border,
  align = 'center',
  justify = 'start',
  width = 'standard',
  removePadding = false,
}: LayoutCellProps) => (
  <div
    className={clsx([
      'flex flex-col h-full items-start overflow-y-scroll scrollbar-hide',
      !removePadding && 'p-f1',
      width === 'standard' && kBoxWidth,
      width === 'half' && kHalfBoxWidth,
      width === 'full' && 'w-full',
      border === 'left' && 'border-main-200 border-l-[1px]',
      border === 'right' && 'border-main-200 border-r-[1px]',
      border === 'none' && 'border-none',
      align === 'top' && 'justify-start',
      align === 'center' && 'justify-center',
      align === 'bottom' && 'justify-end',
      justify === 'start' && 'items-start',
      justify === 'center' && 'items-center',
      justify === 'end' && 'items-end',
    ])}
  >
    {children}
  </div>
);

type LayoutRowProps = {
  isFirst?: boolean;
  height: 'small' | 'medium' | 'large' | 'xl' | 'fit';
  justify?: 'between' | 'start' | 'end';
  hide?: boolean;
  children: React.ReactNode;
  flexDirection?: 'row' | 'column';
  'data-testid'?: string;
};

export const LayoutRow = ({
  isFirst = false,
  height,
  justify = 'between',
  hide = false,
  flexDirection = 'row',
  children,
  'data-testid': dataTestId,
}: LayoutRowProps) => (
  <>
    {!hide && (
      <div
        data-testid={dataTestId}
        className={clsx([
          'flex w-full items-start',
          flexDirection === 'row' && 'flex-row',
          flexDirection === 'column' && 'flex-col',
          'border-b-[1px] border-main-200 transition-opacity duration-300',
          isFirst && 'border-t-[1px] border-main-200',
          justify === 'between' && 'justify-between',
          justify === 'start' && 'justify-start',
          justify === 'end' && 'justify-end',
          height === 'small' && 'h-f4',
          height === 'medium' && 'h-f8',
          height === 'large' && 'h-f12',
          height === 'xl' && 'h-f17',
          height === 'fit' && 'h-fit',
        ])}
      >
        {children}
      </div>
    )}
  </>
);
